'use client';

import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import type { Pet } from './PetCard';

interface ComparisonPanelProps {
  pets: Pet[];
  onRemove: (petId: string) => void;
  onClear: () => void;
}

const ComparisonPanel = ({ pets, onRemove, onClear }: ComparisonPanelProps) => {
  if (pets.length === 0) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-card border-t-2 border-primary shadow-elevated z-40 transition-transform duration-300">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="ScaleIcon" size={24} variant="solid" className="text-primary" />
            <h3 className="text-lg font-headline font-semibold text-foreground">
              Compare Pets ({pets.length}/3)
            </h3>
          </div>
          <button
            onClick={onClear}
            className="px-4 py-2 text-sm font-semibold text-foreground hover:text-error transition-colors flex items-center space-x-1"
          >
            <Icon name="XMarkIcon" size={20} variant="outline" />
            <span>Clear All</span>
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pets.map((pet) => (
            <div key={pet.id} className="bg-background rounded-lg p-3 border border-border relative">
              <button
                onClick={() => onRemove(pet.id)}
                className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-error hover:text-white transition-colors"
                aria-label="Remove from comparison"
              >
                <Icon name="XMarkIcon" size={16} variant="outline" />
              </button>
              <div className="flex space-x-3">
                <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                  <AppImage
                    src={pet.image}
                    alt={pet.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-foreground truncate">{pet.name}</h4>
                  <p className="text-sm text-muted-foreground truncate">{pet.breed}</p>
                  <p className="text-xs text-muted-foreground mt-1">{pet.age} • {pet.size}</p>
                  <div className="flex items-center space-x-1 mt-1">
                    <Icon name="SparklesIcon" size={14} variant="solid" className="text-brand-cta" />
                    <span className="text-xs font-semibold text-brand-cta">{pet.compatibilityScore}% Match</span>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {pets.length < 3 && (
            <div className="bg-muted rounded-lg p-3 border-2 border-dashed border-border flex items-center justify-center">
              <div className="text-center">
                <Icon name="PlusCircleIcon" size={32} variant="outline" className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Add up to {3 - pets.length} more</p>
              </div>
            </div>
          )}
        </div>

        {pets.length >= 2 && (
          <div className="mt-4 flex justify-center">
            <button className="px-6 py-3 bg-brand-cta text-white font-headline font-semibold rounded-lg hover:shadow-elevated hover:-translate-y-0.5 transition-all duration-300 flex items-center space-x-2">
              <Icon name="ChartBarIcon" size={20} variant="outline" />
              <span>View Detailed Comparison</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparisonPanel;